import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { eventsApi, getFileUrl } from "../lib/api";
import {
  Camera, ArrowLeft, Music, Upload, Play, CheckCircle, Loader2,
  Film, Sparkles, Download, ExternalLink, Home, QrCode, CreditCard
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function VideoCustomizePage() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const isPaid = searchParams.get("paid") === "true";

  const [event, setEvent] = useState(null);
  const [musicList, setMusicList] = useState([]);
  const [selectedMusic, setSelectedMusic] = useState("party-beat");
  const [customMusicName, setCustomMusicName] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoPath, setVideoPath] = useState(null);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const musicInputRef = useRef(null);

  useEffect(() => {
    loadData();
    if (sessionId && isPaid) {
      verifyPayment();
    }
  }, [eventId]);

  const loadData = async () => {
    try {
      const [eventRes, musicRes] = await Promise.all([
        eventsApi.get(eventId),
        eventsApi.getMusicLibrary(),
      ]);
      setEvent(eventRes.data);
      setMusicList(musicRes.data);
      if (eventRes.data.custom_video_path) {
        setVideoReady(true);
        setVideoPath(eventRes.data.custom_video_path);
        setPaymentVerified(true);
      }
      if (eventRes.data.has_video_purchase) {
        setPaymentVerified(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const verifyPayment = async (attempts = 0) => {
    if (attempts >= 8) return;
    setCheckingPayment(true);
    try {
      const { data } = await eventsApi.paymentStatus(sessionId);
      if (data.payment_status === "paid") {
        setPaymentVerified(true);
        setCheckingPayment(false);
        return;
      }
      setTimeout(() => verifyPayment(attempts + 1), 2500);
    } catch {
      setTimeout(() => verifyPayment(attempts + 1), 2500);
    }
  };

  const handleCheckout = async (method = "card") => {
    setCheckingPayment(false);
    try {
      const originUrl = window.location.origin;
      const apiCall = method === "pix" ? eventsApi.pixVideoCheckout : eventsApi.videoCheckout;
      const { data } = await apiCall(eventId, originUrl, selectedMusic);
      if (data.url) window.location.href = data.url;
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Erro ao iniciar pagamento. Tente novamente.";
      try { alert(msg); } catch (_) {}
    }
  };

  const handleUploadMusic = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMusic(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await eventsApi.uploadMusic(eventId, formData);
      setCustomMusicName(file.name);
      setSelectedMusic("custom");
    } catch (err) {
      console.error(err);
    } finally {
      setUploadingMusic(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await eventsApi.generateReels(eventId, selectedMusic === "custom" ? null : selectedMusic);
      setVideoPath(data.video_path);
      setVideoReady(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao gerar video";
      alert(msg);
    } finally {
      setGenerating(false);
    }
  };

  const genreEmoji = { "Eletronica": "electric", "Acustico": "acoustic", "Lo-Fi": "lofi", "Samba": "samba", "Classico": "classic" };

  return (
    <div className="min-h-screen bg-[#FFFBF7]" data-testid="video-customize-page">
      <header className="sticky top-0 w-full bg-[#FFFBF7]/80 backdrop-blur-xl border-b-2 border-[#1C1917] z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(`/feed/${eventId}`)}
            className="w-9 h-9 flex items-center justify-center bg-white border-2 border-[#1C1917] rounded-full shadow-[2px_2px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] active:translate-y-[2px] active:shadow-none transition-all"
            data-testid="back-to-feed"
            aria-label="Voltar ao feed"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 bg-[#8B5CF6] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shadow-[2px_2px_0px_#1C1917] shrink-0">
              <Film className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-tight text-[#1C1917] truncate" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Video Personalizado
            </span>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="hidden sm:inline-flex px-3 py-1.5 items-center gap-1.5 text-xs font-bold bg-white border-2 border-[#1C1917] rounded-full shadow-[2px_2px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] active:translate-y-[2px] active:shadow-none transition-all"
            data-testid="back-to-dashboard"
            aria-label="Ir para o dashboard"
          >
            <Home className="w-3.5 h-3.5" strokeWidth={2.5} />
            Dashboard
          </button>
          <button
            onClick={() => navigate("/dashboard")}
            className="sm:hidden w-9 h-9 flex items-center justify-center bg-white border-2 border-[#1C1917] rounded-full shadow-[2px_2px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] active:translate-y-[2px] active:shadow-none transition-all"
            data-testid="back-to-dashboard-mobile"
            aria-label="Ir para o dashboard"
          >
            <Home className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* If video already generated */}
        {videoReady && videoPath && (
          <div className="bg-white border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#8B5CF6] to-[#FF6B6B] p-4 text-white text-center">
              <CheckCircle className="w-8 h-8 mx-auto mb-2" strokeWidth={2.5} />
              <h2 className="text-xl font-black" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Video Pronto!
              </h2>
            </div>
            <div className="p-4">
              <video
                src={getFileUrl(videoPath)}
                controls
                playsInline
                className="w-full rounded-xl border-2 border-[#E7E5E4]"
                style={{ maxHeight: "500px" }}
                data-testid="reels-video-player"
              />
              <div className="flex gap-2 mt-4">
                <a
                  href={getFileUrl(videoPath)}
                  download
                  className="flex-1 bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[3px_3px_0px_#1C1917] rounded-full font-bold py-3 text-center text-sm flex items-center justify-center gap-1 hover:translate-y-[2px] hover:shadow-[1px_1px_0px_#1C1917] transition-all"
                  data-testid="download-video-btn"
                >
                  <Download className="w-4 h-4" strokeWidth={2.5} /> Baixar Video
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Payment + Music Selection */}
        {!videoReady && (
          <>
            {/* Step indicator */}
            <div className="text-center">
              <h1 className="text-2xl font-black text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Crie seu Reels da Festa
              </h1>
              <p className="text-sm text-[#78716C] mt-1">
                Video estilo TikTok de 30-60s com as melhores fotos + musica
              </p>
            </div>

            {/* Music Selection */}
            <div className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-5">
              <h3 className="font-bold text-[#1C1917] mb-3 flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                <Music className="w-5 h-5 text-[#8B5CF6]" strokeWidth={2.5} />
                Escolha a Musica
              </h3>

              <div className="space-y-2">
                {musicList.map((track) => (
                  <button
                    key={track.id}
                    onClick={() => setSelectedMusic(track.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left ${
                      selectedMusic === track.id
                        ? "border-[#8B5CF6] bg-[#8B5CF6]/5 shadow-[2px_2px_0px_#8B5CF6]"
                        : "border-[#E7E5E4] hover:border-[#1C1917]"
                    }`}
                    data-testid={`music-${track.id}`}
                  >
                    <div>
                      <p className="font-bold text-sm text-[#1C1917]">{track.name}</p>
                      <p className="text-xs text-[#78716C]">{track.genre} - {track.duration}s</p>
                    </div>
                    {selectedMusic === track.id && (
                      <div className="w-6 h-6 bg-[#8B5CF6] rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-white" strokeWidth={2.5} />
                      </div>
                    )}
                  </button>
                ))}

                {/* Custom upload */}
                <button
                  onClick={() => musicInputRef.current?.click()}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left ${
                    selectedMusic === "custom"
                      ? "border-[#FF6B6B] bg-[#FF6B6B]/5 shadow-[2px_2px_0px_#FF6B6B]"
                      : "border-dashed border-[#E7E5E4] hover:border-[#1C1917]"
                  }`}
                  data-testid="upload-music-btn"
                >
                  <div className="flex items-center gap-2">
                    {uploadingMusic ? <Loader2 className="w-5 h-5 animate-spin text-[#FF6B6B]" /> : <Upload className="w-5 h-5 text-[#FF6B6B]" strokeWidth={2.5} />}
                    <div>
                      <p className="font-bold text-sm text-[#1C1917]">
                        {customMusicName || "Enviar sua musica"}
                      </p>
                      <p className="text-xs text-[#78716C]">MP3 (max 20MB)</p>
                    </div>
                  </div>
                  {selectedMusic === "custom" && (
                    <div className="w-6 h-6 bg-[#FF6B6B] rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-white" strokeWidth={2.5} />
                    </div>
                  )}
                </button>
                <input
                  ref={musicInputRef}
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={handleUploadMusic}
                />
              </div>
            </div>

            {/* Price + CTA */}
            {!paymentVerified ? (
              <div className="bg-white border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl p-5">
                <div className="bg-[#FFFBF7] border-2 border-[#E7E5E4] rounded-xl p-4 text-center mb-4">
                  <p className="text-xs text-[#78716C] font-bold uppercase">Video Personalizado</p>
                  <p className="text-3xl font-black text-[#1C1917] mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    R$ 49,90
                  </p>
                  <p className="text-xs text-[#78716C] mt-1">pagamento unico</p>
                </div>
                <ul className="space-y-1.5 mb-4">
                  {["Video vertical estilo Reels (30-60s)", "Melhores fotos com efeitos de zoom", "Trilha sonora a sua escolha", "Download em alta qualidade"].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-[#44403C]">
                      <CheckCircle className="w-4 h-4 text-[#8B5CF6] shrink-0" strokeWidth={2.5} />
                      {t}
                    </li>
                  ))}
                </ul>
                <div className="space-y-2">
                  <Button
                    onClick={() => handleCheckout("pix")}
                    className="w-full bg-[#00B894] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 text-base hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all"
                    data-testid="buy-video-pix-btn"
                  >
                    <QrCode className="w-5 h-5 mr-2" strokeWidth={2.5} />
                    Pagar com Pix
                  </Button>
                  <Button
                    onClick={() => handleCheckout("card")}
                    className="w-full bg-[#8B5CF6] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 text-base hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all"
                    data-testid="buy-video-card-btn"
                  >
                    <CreditCard className="w-5 h-5 mr-2" strokeWidth={2.5} />
                    Pagar com Cartão
                  </Button>
                </div>
              </div>
            ) : checkingPayment ? (
              <div className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-8 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-[#8B5CF6] mx-auto mb-3" />
                <p className="font-bold text-[#1C1917]">Verificando pagamento...</p>
              </div>
            ) : (
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-4 text-base hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all"
                data-testid="generate-reels-btn"
              >
                {generating ? (
                  <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Gerando seu video...</>
                ) : (
                  <><Film className="w-5 h-5 mr-2" strokeWidth={2.5} /> Gerar Meu Reels</>
                )}
              </Button>
            )}
          </>
        )}

        {/* Regenerate option */}
        {videoReady && paymentVerified && (
          <Button
            onClick={handleGenerate}
            disabled={generating}
            variant="outline"
            className="w-full border-2 border-[#1C1917] rounded-full font-bold shadow-[2px_2px_0px_#1C1917]"
            data-testid="regenerate-video-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Film className="w-4 h-4 mr-2" />}
            Regerar com outra musica
          </Button>
        )}
      </div>
    </div>
  );
}
